import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { t } from '@/i18n';
import { api, errorField, errorMessage } from '@/lib/api';
import { useSetMe } from '@/lib/auth';
import type { AuthResponse } from '@/lib/types';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { AuthLayout } from './AuthLayout';
import { PasswordInput, safeNext } from './LoginPage';
import { OAuthButtons } from './OAuthButtons';

export function RegisterPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const setMe = useSetMe();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<{ message: string; field: string | null } | null>(null);
  const [loading, setLoading] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const res = await api<AuthResponse>('/api/auth/register', { body: { name, email, password } });
      setMe(res);
      navigate(safeNext(params.get('next')), { replace: true });
    } catch (err) {
      setError({ message: errorMessage(err), field: errorField(err) });
    } finally {
      setLoading(false);
    }
  };

  const fieldError = (f: string) => (error?.field === f ? error.message : null);

  return (
    <AuthLayout
      title={t('auth.registerTitle')}
      subtitle={t('auth.registerSubtitle')}
      footer={
        <>
          {t('auth.haveAccount')}{' '}
          <Link to={`/login${params.get('next') ? `?next=${encodeURIComponent(params.get('next')!)}` : ''}`} className="font-medium text-accent-fg hover:underline">
            {t('auth.login')}
          </Link>
        </>
      }
    >
      <OAuthButtons next={params.get('next')} />
      <form onSubmit={submit} className="flex flex-col gap-4" noValidate>
        <Input label={t('auth.name')} placeholder={t('auth.namePlaceholder')} value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" required autoFocus error={fieldError('name')} />
        <Input label={t('auth.email')} type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" required error={fieldError('email')} />
        <PasswordInput label={t('auth.password')} value={password} onChange={setPassword} autoComplete="new-password" hint={t('auth.passwordHint')} error={fieldError('password')} />
        {error && !['name', 'email', 'password'].includes(error.field ?? '') && (
          <p role="alert" className="rounded-control bg-danger-soft px-3 py-2 text-sm text-danger dark:bg-danger/15">
            {error.message}
          </p>
        )}
        <Button type="submit" variant="primary" size="lg" loading={loading}>
          {t('auth.register')}
        </Button>
      </form>
    </AuthLayout>
  );
}
